import type {
  GeometryGroupModel,
  ManifestBundle,
  ManifestObjectId,
} from "@flexcompute/uvf";

const GEOMETRY_GROUP_TYPE = "GeometryGroup" as const;

/**
 * Yield every ManifestObjectId in the bundle's geometry tree that the
 * viewer needs to render before we can call `focus()`.
 */
export function* getIdsToWaitFor(
  bundle: ManifestBundle,
): IterableIterator<ManifestObjectId> {
  try {
    const lookup = new Map(bundle.map((m) => [m.id, m]));
    const rootGroup = bundle.find(
      ({ type, properties }) =>
        type === GEOMETRY_GROUP_TYPE &&
        (!properties ||
          !("type" in properties) ||
          (properties as { type?: number }).type === 0),
    ) as GeometryGroupModel | undefined;

    if (!rootGroup) return;

    const stack: GeometryGroupModel[] = [rootGroup];
    const visited = new Set<ManifestObjectId>([rootGroup.id]);
    yield rootGroup.id;

    let current: GeometryGroupModel | undefined;
    while ((current = stack.pop())) {
      for (const childId of (current.attributions?.members as
        | ManifestObjectId[]
        | undefined) ?? []) {
        const model = lookup.get(childId);
        if (!model || visited.has(model.id)) continue;
        visited.add(model.id);
        if (model.type === GEOMETRY_GROUP_TYPE) {
          stack.push(model as GeometryGroupModel);
        }
        yield childId;
      }
    }
  } catch {
    // Malformed bundle — return empty
  }
}

/**
 * IDs to hide after the initial `viewer.show(allInstanceIds)`.
 *
 * Flow360 manifests include farfield bounding-box faces, auto-slice planes,
 * and Q-criterion isosurfaces which obscure the actual surface geometry.
 * The SimCloud portal hides these by default; we do the same.
 */
const HIDDEN_GROUPS = new Set(["slices", "isosurfaces"]);
const HIDDEN_ID_PATTERNS = [/^Boundaries \(Auto\)/, /^autoSlice_/, /^Slices \(Auto\)/, /^Isosurfaces \(Auto\)/];

export function getDefaultHiddenIds(
  bundle: ManifestBundle,
): Set<ManifestObjectId> {
  const hidden = new Set<ManifestObjectId>();
  const lookup = new Map(bundle.map((m) => [m.id, m]));

  for (const model of bundle) {
    const id = model.id as string;
    if (HIDDEN_GROUPS.has(id)) {
      collectSubtree(id as ManifestObjectId, lookup, hidden);
      continue;
    }
    if (HIDDEN_ID_PATTERNS.some((p) => p.test(id))) {
      collectSubtree(id as ManifestObjectId, lookup, hidden);
    }
  }
  return hidden;
}

function collectSubtree(
  rootId: ManifestObjectId,
  lookup: Map<ManifestObjectId, ManifestBundle[number]>,
  out: Set<ManifestObjectId>,
): void {
  out.add(rootId);
  const model = lookup.get(rootId);
  if (!model || model.type !== GEOMETRY_GROUP_TYPE) return;
  const members = (model as GeometryGroupModel).attributions?.members as
    | ManifestObjectId[]
    | undefined;
  if (!members) return;
  for (const childId of members) {
    if (!out.has(childId)) collectSubtree(childId, lookup, out);
  }
}

// ---------------------------------------------------------------------------
// Layer tree — top-level toggleable groups for the LayerSidebar
// ---------------------------------------------------------------------------

export interface ManifestLayer {
  id: string;
  label: string;
  /** Model type from the manifest (e.g. "SolidGeometry", "GeometryGroup"). */
  modelType?: string;
  defaultVisible: boolean;
  /** Child items for expandable groups (slices, isosurfaces). */
  children?: ManifestLayer[];
}

/** Parsed metadata for an individual isosurface in the manifest. */
export interface IsosurfaceInfo {
  id: string;
  label: string;
  /** e.g. "qcriterion" */
  criterionField?: string;
  /** e.g. 0.0008494 */
  criterionValue?: number;
}

// ---------------------------------------------------------------------------
// Surface instance discovery — for field coloring / wireframe / LIC
// ---------------------------------------------------------------------------

/**
 * Probe every scene instance with getOrCreateFieldController.  It throws for
 * non-surface (non-packed-geometry) instances, so we catch and skip.
 *
 * To prevent the packedGeometryFactory from auto-applying fields (which
 * causes WebGL feedback loops), we immediately clear the auto-set fieldName
 * on newly created controllers.
 */
export function discoverSurfaceInstanceIds(viewer: any): unknown[] {
  const ids: unknown[] = [];
  for (const instanceId of viewer.scene.allInstanceIds) {
    try {
      const fc = viewer.getOrCreateFieldController(instanceId);
      fc.fieldName.set(undefined);
      ids.push(instanceId);
    } catch {
      // Not a SolidGeometry / SurfaceQuilt — skip
    }
  }
  return ids;
}

/**
 * Collect field names from FieldControllers on the given surface instance IDs.
 */
export function getAvailableFields(
  viewer: any,
  surfaceIds: unknown[],
): string[] {
  const names = new Set<string>();
  for (const iid of surfaceIds) {
    try {
      const fc = viewer.getOrCreateFieldController(iid);
      if (!fc?.fieldNames) continue;
      for (const n of fc.fieldNames) names.add(n);
    } catch {
      // skip
    }
  }
  return [...names].sort();
}

/**
 * Return the list of available colormap names from FieldController statics.
 * Requires the FieldController class (imported dynamically).
 */
export function getAvailableColormaps(FieldController: any): string[] {
  try {
    return [...FieldController.availableColormaps()];
  } catch {
    return [];
  }
}

/**
 * Recursively collect leaf (non-group) children from a set of member IDs.
 * Intermediate GeometryGroup nodes are flattened away so the UI lists
 * only the renderable geometry items.
 */
function collectLeafMembers(
  memberIds: ManifestObjectId[],
  lookup: Map<ManifestObjectId, ManifestBundle[number]>,
  hiddenIds: Set<ManifestObjectId>,
): ManifestLayer[] {
  const leaves: ManifestLayer[] = [];
  for (const cid of memberIds) {
    const child = lookup.get(cid);
    if (child?.type === GEOMETRY_GROUP_TYPE) {
      const subMembers = (child as GeometryGroupModel).attributions
        ?.members as ManifestObjectId[] | undefined;
      if (subMembers && subMembers.length > 0) {
        leaves.push(...collectLeafMembers(subMembers, lookup, hiddenIds));
        continue;
      }
    }
    const childLabel =
      (child?.properties as { name?: string } | undefined)?.name ??
      (cid as string);
    leaves.push({
      id: cid as string,
      label: childLabel,
      modelType: child?.type as string | undefined,
      defaultVisible: !hiddenIds.has(cid),
    });
  }
  return leaves;
}

/**
 * Extract the top-level manifest groups suitable for a layer-toggle UI.
 *
 * Walks the first level of the root geometry group and returns one entry
 * per direct child group (e.g. "Surface output", "Boundaries (Auto)",
 * "Slices (Auto)", "Isosurfaces (Auto)").  Groups that contain child
 * members (slices, isosurfaces) populate a `children` array so the
 * sidebar can render them as expandable trees.  Intermediate sub-groups
 * are flattened so the children list contains only renderable geometry.
 */
export function getManifestLayers(bundle: ManifestBundle): ManifestLayer[] {
  const lookup = new Map(bundle.map((m) => [m.id, m]));
  const rootGroup = bundle.find(
    ({ type, properties }) =>
      type === GEOMETRY_GROUP_TYPE &&
      (!properties ||
        !("type" in properties) ||
        (properties as { type?: number }).type === 0),
  ) as GeometryGroupModel | undefined;

  if (!rootGroup) return [];

  const members = (rootGroup.attributions?.members as
    | ManifestObjectId[]
    | undefined) ?? [];

  const hiddenIds = getDefaultHiddenIds(bundle);

  return members
    .map((childId) => {
      const model = lookup.get(childId);
      const label =
        (model?.properties as { name?: string } | undefined)?.name ??
        (childId as string);

      let children: ManifestLayer[] | undefined;
      if (model?.type === GEOMETRY_GROUP_TYPE) {
        const groupMembers = (model as GeometryGroupModel).attributions
          ?.members as ManifestObjectId[] | undefined;
        if (groupMembers && groupMembers.length > 0) {
          children = collectLeafMembers(groupMembers, lookup, hiddenIds);
        }
      }

      return {
        id: childId as string,
        label,
        modelType: model?.type as string | undefined,
        defaultVisible: !hiddenIds.has(childId),
        children,
      };
    })
    .filter((l) => l.label !== "root");
}

/**
 * Collect all descendant ManifestObjectIds under a root model ID,
 * recursing into GeometryGroup subtrees.  Used by the viewer to
 * show/hide all geometry under a group toggle.
 */
export function collectDescendantIds(
  rootId: ManifestObjectId,
  bundle: ManifestBundle,
): ManifestObjectId[] {
  const lookup = new Map(bundle.map((m) => [m.id, m]));
  const result: ManifestObjectId[] = [];
  const stack: ManifestObjectId[] = [rootId];
  const visited = new Set<ManifestObjectId>();
  while (stack.length > 0) {
    const current = stack.pop()!;
    if (visited.has(current)) continue;
    visited.add(current);
    result.push(current);
    const model = lookup.get(current);
    if (model?.type === GEOMETRY_GROUP_TYPE) {
      const sub = (model as GeometryGroupModel).attributions?.members as
        | ManifestObjectId[]
        | undefined;
      if (sub) stack.push(...sub);
    }
  }
  return result;
}

/**
 * Extract metadata for individual isosurfaces from the manifest.
 * Parses the name property which typically looks like
 * "qcriterion, qcriterion:0.0008494".
 */
export function getIsosurfaceInfo(bundle: ManifestBundle): IsosurfaceInfo[] {
  const lookup = new Map(bundle.map((m) => [m.id, m]));
  const results: IsosurfaceInfo[] = [];

  for (const model of bundle) {
    const id = model.id as string;
    if (!id.startsWith("autoSlice_") && model.type !== "Isosurface") {
      const name = (model.properties as { name?: string } | undefined)?.name;
      if (!name) continue;
      // Check if this is inside an isosurface group
      const isIso = /^Isosurfaces \(Auto\)/.test(id) ||
        HIDDEN_ID_PATTERNS.some((p) => p.test(id));
      if (!isIso) continue;
    }

    if (model.type === GEOMETRY_GROUP_TYPE) continue;

    const name =
      (model.properties as { name?: string } | undefined)?.name ?? id;
    const info: IsosurfaceInfo = { id, label: name };

    // Parse "field, field:value" pattern
    const match = name.match(/^(\w+),\s*\w+:([\d.eE+-]+)$/);
    if (match) {
      info.criterionField = match[1];
      info.criterionValue = parseFloat(match[2]!);
    }

    results.push(info);
  }

  // Also walk isosurface groups for children
  for (const model of bundle) {
    if (model.type !== GEOMETRY_GROUP_TYPE) continue;
    const id = model.id as string;
    if (!/isosurface/i.test(id)) continue;
    const groupMembers = (model as GeometryGroupModel).attributions
      ?.members as ManifestObjectId[] | undefined;
    if (!groupMembers) continue;
    for (const cid of groupMembers) {
      if (results.some((r) => r.id === (cid as string))) continue;
      const child = lookup.get(cid);
      if (!child || child.type === GEOMETRY_GROUP_TYPE) continue;
      const childName =
        (child.properties as { name?: string } | undefined)?.name ??
        (cid as string);
      const info: IsosurfaceInfo = { id: cid as string, label: childName };
      const match = childName.match(/^(\w+),\s*\w+:([\d.eE+-]+)$/);
      if (match) {
        info.criterionField = match[1];
        info.criterionValue = parseFloat(match[2]!);
      }
      results.push(info);
    }
  }

  return results;
}
